/**
 * Utility constants and functions
 */
export const MAX_INT = 2147483647;
export const MIN_INT = -2147483648;
export const MAX_LONG = Number.MAX_SAFE_INTEGER;
export const MIN_LONG = Number.MIN_SAFE_INTEGER;
// special value to identify missing items. treated differently from undefined
const MISSING = Symbol("missing");
const CYCLE_FOUND_ERROR = Object.freeze(new Error("mingo: cycle detected while processing object/array"));
const ARRAY_PROTO = Object.getPrototypeOf([]);
const OBJECT_PROTO = Object.getPrototypeOf({});
const OBJECT_TAG = "[object Object]";
const OBJECT_TYPE_RE = /^\[object ([a-zA-Z0-9]+)\]$/;
class Null {
}
class Undefined {
}
const getConstructor = (v) => {
    if (v === null)
        return Null;
    if (v === undefined)
        return Undefined;
    return v.constructor;
};
/**
 * Uses the simple hash method as described in Effective Java.
 * @see https://stackoverflow.com/a/113600/1370481
 * @param value The value to hash
 * @returns {number}
 */
const DEFAULT_HASH_FUNCTION = (value) => {
    const s = stringify(value);
    let hash = 0;
    let i = s.length;
    while (i)
        hash = ((hash << 5) - hash) ^ s.charCodeAt(--i);
    return hash >>> 0;
};
// no array, object, or function types
const JS_SIMPLE_TYPES = new Set([
    "null",
    "undefined",
    "boolean",
    "number",
    "string",
    "date",
    "regexp"
]);
const IMMUTABLE_TYPES_SET = new Set([Undefined, Null, Boolean, String, Number]);
/** Convert simple value to string representation. */
const toString = (v) => v.toString(); // eslint-disable-line @typescript-eslint/no-base-to-string
/** Convert a typed array to string representation. */
const typedArrayToString = (v) => `${getConstructor(v).name}[${v.toString()}]`; // eslint-disable-line @typescript-eslint/no-base-to-string
/** Map of constructors to string converter functions */
const STRING_CONVERTERS = new Map([
    [Number, toString],
    [Boolean, toString],
    [RegExp, toString],
    [Function, toString],
    [Symbol, toString],
    [BigInt, (n) => "0x" + n.toString(16)],
    [Date, (d) => d.toISOString()],
    [String, JSON.stringify],
    [Null, (_) => "null"],
    [Undefined, (_) => "undefined"],
    [Int8Array, typedArrayToString],
    [Uint8Array, typedArrayToString],
    [Uint8ClampedArray, typedArrayToString],
    [Int16Array, typedArrayToString],
    [Uint16Array, typedArrayToString],
    [Int32Array, typedArrayToString],
    [Uint32Array, typedArrayToString],
    [Float32Array, typedArrayToString],
    [Float64Array, typedArrayToString],
    [BigInt64Array, typedArrayToString],
    [BigUint64Array, typedArrayToString]
]);
/** MongoDB sort comparison order. https://www.mongodb.com/docs/manual/reference/bson-type-comparison-order */
const SORT_ORDER_BY_TYPE = {
    null: 0,
    undefined: 0,
    number: 1,
    string: 2,
    object: 3,
    array: 4,
    boolean: 5,
    date: 6,
    regexp: 7,
    function: 8
};
/**
 * Compare function which adheres to MongoDB comparison order.
 *
 * @param a The first value
 * @param b The second value
 * @returns {Number}
 */
export const compare = (a, b) => {
    if (a === MISSING)
        a = undefined;
    if (b === MISSING)
        b = undefined;
    const [u, v] = [a, b].map(n => SORT_ORDER_BY_TYPE[getType(n).toLowerCase()]);
    if (u !== v)
        return u - v;
    // number | string | date
    if (u === 1 || u === 2 || u === 6) {
        if (a < b)
            return -1;
        if (a > b)
            return 1;
        return 0;
    }
    // check for equivalence equality
    if (isEqual(a, b))
        return 0;
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    // if we get here we are comparing a type that does not make sense.
    return 0;
};
export function assert(condition, message) {
    if (!condition)
        throw new Error(message);
}
const isTypedArray = (v) => {
    const proto = Object.getPrototypeOf(getConstructor(v));
    return proto && proto.name === "TypedArray";
};
/**
 * Deep clone an object. Value types and immutable objects are returned as is.
 */
export const cloneDeep = (obj) => {
    if (IMMUTABLE_TYPES_SET.has(getConstructor(obj)))
        return obj;
    const cycle = new Set();
    const clone = (val) => {
        if (cycle.has(val))
            throw CYCLE_FOUND_ERROR;
        const ctor = getConstructor(val);
        if (IMMUTABLE_TYPES_SET.has(ctor))
            return val;
        try {
            // arrays
            if (isArray(val)) {
                cycle.add(val);
                return val.map(clone);
            }
            // object literals
            if (isObject(val)) {
                cycle.add(val);
                const res = {};
                for (const k in val)
                    res[k] = clone(val[k]);
                return res;
            }
        }
        finally {
            cycle.delete(val);
        }
        // dates, regex, typed arrays
        if (ctor === Date || ctor === RegExp || isTypedArray(val)) {
            return new ctor(val);
        }
        return val;
    };
    return clone(obj);
};
/**
 * Returns the name of type as specified in the tag returned by a call to Object.prototype.toString
 * @param v A value
 */
export const getType = (v) => OBJECT_TYPE_RE.exec(Object.prototype.toString.call(v))[1];
export const isBoolean = (v) => typeof v === "boolean";
export const isString = (v) => typeof v === "string";
export const isSymbol = (v) => typeof v === "symbol";
export const isNumber = (v) => !isNaN(v) && typeof v === "number";
export const isBigInt = (v) => !isNaN(v) && typeof v === "bigint";
export const isNotNaN = (v) => !(isNaN(v) && typeof v === "number");
export const isArray = Array.isArray;
export const isObject = (v) => {
    if (!v)
        return false;
    const proto = Object.getPrototypeOf(v);
    return ((proto === OBJECT_PROTO || proto === null) &&
        OBJECT_TAG === Object.prototype.toString.call(v));
};
//  objects, arrays, functions, date, custom object
export const isObjectLike = (v) => v === Object(v);
export const isDate = (v) => v instanceof Date;
export const isRegExp = (v) => v instanceof RegExp;
export const isFunction = (v) => typeof v === "function";
export const isNil = (v) => v === null || v === undefined;
export const inArray = (arr, item) => arr.includes(item);
export const notInArray = (arr, item) => !inArray(arr, item);
export const truthy = (arg, strict = true) => !!arg || (strict && arg === "");
export const isEmpty = (x) => isNil(x) ||
    (isString(x) && !x) ||
    (x instanceof Array && x.length === 0) ||
    (isObject(x) && Object.keys(x).length === 0);
export const isMissing = (v) => v === MISSING;
/** ensure a value is an array or wrapped within one. */
export const ensureArray = (x) => x instanceof Array ? x : [x];
export const has = (obj, prop) => !!obj && Object.prototype.hasOwnProperty.call(obj, prop);
const mergeable = (left, right) => (isObject(left) && isObject(right)) || (isArray(left) && isArray(right));
/**
 * Deep merge objects or arrays.
 * When the inputs have unmergeable types, the  right hand value is returned.
 * If inputs are arrays and options.flatten is set, elements in the same position are merged together. Remaining elements are appended to the target object.
 * If options.flatten is false, the right hand value is just appended to the left-hand value.
 * @param target {Object|Array} the target to merge into
 * @param obj {Object|Array} the source object
 */
export function merge(target, obj, options) {
    // default options
    options = options || { flatten: false };
    // take care of missing inputs
    if (isMissing(target) || isNil(target))
        return obj;
    if (isMissing(obj) || isNil(obj))
        return target;
    // fail only on initial input.
    if (!mergeable(target, obj)) {
        if (options.skipValidation)
            return obj || target;
        throw Error("mismatched types. must both be array or object");
    }
    // skip validation after initial input.
    options.skipValidation = true;
    if (isArray(target)) {
        const result = target;
        const input = obj;
        if (options.flatten) {
            let i = 0;
            let j = 0;
            while (i < result.length && j < input.length) {
                result[i] = merge(result[i++], input[j++], options);
            }
            while (j < input.length) {
                result.push(obj[j++]);
            }
        }
        else {
            into(result, input);
        }
    }
    else {
        for (const k in obj) {
            target[k] = merge(target[k], obj[k], options);
        }
    }
    return target;
}
function buildHashIndex(arr, hashFunction = DEFAULT_HASH_FUNCTION) {
    const map = new Map();
    arr.forEach((o, i) => {
        const h = hashCode(o, hashFunction);
        if (map.has(h)) {
            if (!map.get(h).some(j => isEqual(arr[j], o))) {
                map.get(h).push(i);
            }
        }
        else {
            map.set(h, [i]);
        }
    });
    return map;
}
/**
 * Returns the intersection of multiple arrays.
 *
 * @param  {Array} input An array of arrays from which to find intersection.
 * @param  {Function} hashFunction Custom function to hash values, default the hashCode method
 * @return {Array} Array of intersecting values.
 */
export function intersection(input, hashFunction = DEFAULT_HASH_FUNCTION) {
    // if any array is empty, there is no intersection
    if (input.some(arr => arr.length == 0))
        return [];
    if (input.length === 1)
        return Array.from(input);
    // sort input arrays by to get smallest array
    // const sorted = sortBy(input, (a: RawArray) => a.length) as RawArray[];
    const sortedIndex = sortBy(input.map((a, i) => [i, a.length]), (a) => a[1]);
    // get the smallest
    const smallest = input[sortedIndex[0][0]];
    // get hash index of smallest array
    const map = buildHashIndex(smallest, hashFunction);
    // hashIndex for remaining arrays.
    const rmap = new Map();
    // final intersection results and index of first occurrence.
    const results = new Array();
    map.forEach((v, k) => {
        const lhs = v.map(j => smallest[j]);
        const res = lhs.map(_ => 0);
        // used to track first occurence of value in order of the original input array.
        const stable = lhs.map(_ => [sortedIndex[0][0], 0]);
        let found = false;
        for (let i = 1; i < input.length; i++) {
            const [currIndex, _] = sortedIndex[i];
            const arr = input[currIndex];
            if (!rmap.has(i))
                rmap.set(i, buildHashIndex(arr));
            // we found a match. let's confirm.
            if (rmap.get(i).has(k)) {
                const rhs = rmap
                    .get(i)
                    .get(k)
                    .map(j => arr[j]);
                // confirm the intersection with an equivalence check.
                found = lhs
                    .map((s, n) => rhs.some((t, m) => {
                    // we expect only one to match here since these are just collisions.
                    const p = res[n];
                    if (isEqual(s, t)) {
                        res[n]++;
                        // track position of value ordering for stability.
                        if (currIndex < stable[n][0]) {
                            stable[n] = [currIndex, rmap.get(i).get(k)[m]];
                        }
                    }
                    return p < res[n];
                }))
                    .some(Boolean);
            }
            // found nothing, so exclude value. this was just a hash collision.
            if (!found)
                return;
        }
        // extract value into result if we found an intersection.
        // we find an intersection if the frequency counter matches the count of the remaining arrays.
        if (found) {
            into(results, res
                .map((n, i) => {
                return n === input.length - 1 ? [lhs[i], stable[i]] : MISSING;
            })
                .filter(n => n !== MISSING));
        }
    });
    return results
        .sort((a, b) => {
        const [_i, [u, m]] = a;
        const [_j, [v, n]] = b;
        const r = compare(u, v);
        if (r !== 0)
            return r;
        return compare(m, n);
    })
        .map(v => v[0]);
}
/**
 * Flatten the array
 *
 * @param {Array} xs The array to flatten
 * @param {Number} depth The number of nested lists to iterate
 */
export function flatten(xs, depth = 0) {
    const arr = new Array();
    function flatten2(ys, n) {
        for (let i = 0, len = ys.length; i < len; i++) {
            if (isArray(ys[i]) && (n > 0 || n < 0)) {
                flatten2(ys[i], Math.max(-1, n - 1));
            }
            else {
                arr.push(ys[i]);
            }
        }
    }
    flatten2(xs, depth);
    return arr;
}
/** Returns all members of the value in an object literal. */
const getMembersOf = (value) => {
    let [proto, names] = [
        Object.getPrototypeOf(value),
        Object.getOwnPropertyNames(value)
    ];
    // save effective prototype
    let activeProto = proto;
    // traverse the prototype hierarchy until we get property names or hit the bottom prototype.
    while (!names.length && proto !== OBJECT_PROTO && proto !== ARRAY_PROTO) {
        activeProto = proto;
        names = Object.getOwnPropertyNames(proto);
        proto = Object.getPrototypeOf(proto);
    }
    const o = {};
    names.forEach(k => (o[k] = value[k]));
    return [o, activeProto];
};
/**
 * Determine whether two values are the same or strictly equivalent.
 * Checking whether values are the same only applies to built in objects.
 * For user-defined objects this checks for only referential equality so
 * two different instances with the same values are not equal.
 *
 * @param  {*}  a The first value
 * @param  {*}  b The second value
 * @return {Boolean}   Result of comparison
 */
export function isEqual(a, b) {
    const args = [[a, b]];
    while (args.length > 0) {
        [a, b] = args.pop();
        // strictly equal must be equal. matches referentially equal values.
        if (a === b)
            continue;
        // unequal types and functions (unless referentially equivalent) cannot be equal.
        const ctor = getConstructor(a);
        if (ctor !== getConstructor(b) || isFunction(a))
            return false;
        // string convertable types
        if (STRING_CONVERTERS.has(ctor)) {
            const str = STRING_CONVERTERS.get(ctor);
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            if (str(a) !== str(b))
                return false;
            // values are equal, so move next
            continue;
        }
        // handle array and object types
        if (ctor === Array || ctor === Object) {
            const ka = Object.keys(a);
            const kb = Object.keys(b);
            if (ka.length !== kb.length)
                return false;
            if (new Set(ka.concat(kb)).size != ka.length)
                return false;
            for (const k of ka)
                args.push([a[k], b[k]]);
            // move next
            continue;
        }
        // user-defined type detected.
        // we don't try to compare user-defined types (even though we could...shhhh).
        return false;
    }
    // nothing left to compare
    return !args.length;
}
/**
 * Return a new unique version of the collection
 * @param  {Array} input The input collection
 * @return {Array}
 */
export function unique(input, hashFunction = DEFAULT_HASH_FUNCTION) {
    const result = input.map(_ => MISSING);
    buildHashIndex(input, hashFunction).forEach((v, _) => {
        v.forEach(i => (result[i] = input[i]));
    });
    return result.filter(v => v !== MISSING);
}
/**
 * Encode value to string using a simple non-colliding stable scheme.
 * Handles user-defined types by processing keys on first non-empty prototype.
 * If a user-defined type provides a "toJSON" function, it is used.
 *
 * @param value The value to convert to a string representation.
 * @returns {String}
 */
export function stringify(value) {
    const cycle = new Set();
    // stringify with cycle check
    const str = (v) => {
        const ctor = getConstructor(v);
        // string convertable types
        if (STRING_CONVERTERS.has(ctor)) {
            return STRING_CONVERTERS.get(ctor)(v);
        }
        const tag = ctor === Object ? "" : ctor.name;
        // handle JSONable objects.
        if (isFunction(v["toJSON"])) {
            return `${tag}(${JSON.stringify(v)})`;
        }
        // handle cycles
        if (cycle.has(v))
            throw CYCLE_FOUND_ERROR;
        cycle.add(v);
        try {
            // handle array
            if (ctor === Array) {
                return "[" + v.map(str).join(",") + "]";
            }
            // handle user-defined object
            if (ctor !== Object) {
                // handle user-defined types or object literals.
                const [members, _] = getMembersOf(v);
                // custom type derived from array.
                if (isArray(v)) {
                    // include other members as part of array elements.
                    return `${tag}${str([...v, members])}`;
                }
                // get members as literal
                v = members;
            }
            const objKeys = Object.keys(v);
            objKeys.sort();
            return (`${tag}{` +
                objKeys.map(k => `${k}:${str(v[k])}`).join(",") +
                "}");
        }
        finally {
            cycle.delete(v);
        }
    };
    // convert to string
    return str(value);
}
/**
 * Generate hash code
 * This selected function is the result of benchmarking various hash functions.
 * This version performs well and can hash 10^6 documents in ~3s with on average 100 collisions.
 *
 * @param value
 * @returns {number|null}
 */
export function hashCode(value, hashFunction) {
    hashFunction = hashFunction || DEFAULT_HASH_FUNCTION;
    if (isNil(value))
        return null;
    return hashFunction(value).toString();
}
/**
 * Returns a (stably) sorted copy of list, ranked in ascending order by the results of running each value through iteratee
 *
 * This implementation treats null/undefined sort keys as less than every other type
 *
 * @param {Array}   collection
 * @param {Function} keyFn The sort key function used to resolve sort keys
 * @param {Function} comparator The comparator function to use for comparing keys. Defaults to standard comparison via `compare(...)`
 * @return {Array} Returns a new sorted array by the given key and comparator function
 */
export function sortBy(collection, keyFn, comparator = compare) {
    if (isEmpty(collection))
        return collection;
    const sorted = new Array();
    const result = new Array();
    for (let i = 0; i < collection.length; i++) {
        const obj = collection[i];
        const key = keyFn(obj, i);
        if (isNil(key)) {
            result.push(obj);
        }
        else {
            sorted.push([key, obj]);
        }
    }
    // use native array sorting but enforce stableness
    sorted.sort((a, b) => comparator(a[0], b[0]));
    return into(result, sorted.map((o) => o[1]));
}
/**
 * Groups the collection into sets by the returned key
 *
 * @param collection
 * @param keyFn {Function} to compute the group key of an item in the collection
 * @returns {GroupByOutput}
 */
export function groupBy(collection, keyFn, hashFunction = DEFAULT_HASH_FUNCTION) {
    if (collection.length < 1)
        return new Map();
    // map of hash to collided values
    const lookup = new Map();
    // map of raw key values to objects.
    const result = new Map();
    for (let i = 0; i < collection.length; i++) {
        const obj = collection[i];
        const key = keyFn(obj, i);
        const hash = hashCode(key, hashFunction);
        if (hash === null) {
            if (result.has(null)) {
                result.get(null).push(obj);
            }
            else {
                result.set(null, [obj]);
            }
        }
        else {
            // find if we can match a hash for which the value is equivalent.
            // this is used to deal with collisions.
            const existingKey = lookup.has(hash)
                ? lookup.get(hash).find(k => isEqual(k, key))
                : null;
            // collision detected or first time seeing key
            if (isNil(existingKey)) {
                // collision detected or first entry so we create a new group.
                result.set(key, [obj]);
                // upload the lookup with the collided key
                if (lookup.has(hash)) {
                    lookup.get(hash).push(key);
                }
                else {
                    lookup.set(hash, [key]);
                }
            }
            else {
                // key exists
                result.get(existingKey).push(obj);
            }
        }
    }
    return result;
}
// max elements to push.
// See argument limit https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/apply
const MAX_ARRAY_PUSH = 50000;
/**
 * Merge elements into the dest
 *
 * @param {*} target The target object
 * @param {*} rest The array of elements to merge into dest
 */
export function into(target, ...rest) {
    if (target instanceof Array) {
        return rest.reduce(((acc, arr) => {
            // push arrary in batches to handle large inputs
            let i = Math.ceil(arr.length / MAX_ARRAY_PUSH);
            let begin = 0;
            while (i-- > 0) {
                Array.prototype.push.apply(acc, arr.slice(begin, begin + MAX_ARRAY_PUSH));
                begin += MAX_ARRAY_PUSH;
            }
            return acc;
        }), target);
    }
    else {
        // merge objects. same behaviour as Object.assign
        return rest.filter(isObjectLike).reduce((acc, item) => {
            Object.assign(acc, item);
            return acc;
        }, target);
    }
}
/**
 * This is a generic memoization function
 *
 * This implementation uses a cache independent of the function being memoized
 * to allow old values to be garbage collected when the memoized function goes out of scope.
 *
 * @param {*} fn The function object to memoize
 */
export function memoize(fn, hashFunction = DEFAULT_HASH_FUNCTION) {
    return ((memo) => {
        return (...args) => {
            const key = hashCode(args, hashFunction) || "";
            if (!has(memo, key)) {
                memo[key] = fn.apply(this, args);
            }
            return memo[key];
        };
    })({
    /* storage */
    });
}
// mingo internal
/**
 * Retrieve the value of a given key on an object
 * @param obj
 * @param key
 * @returns {*}
 * @private
 */
function getValue(obj, key) {
    return isObjectLike(obj) ? obj[key] : undefined;
}
/**
 * Unwrap a single element array to specified depth
 * @param {Array} arr
 * @param {Number} depth
 */
function unwrap(arr, depth) {
    if (depth < 1)
        return arr;
    while (depth-- && arr.length === 1)
        arr = arr[0];
    return arr;
}
/**
 * Resolve the value of the field (dot separated) on the given object
 * @param obj {Object} the object context
 * @param selector {String} dot separated path to field
 * @returns {*}
 */
export function resolve(obj, selector, options) {
    let depth = 0;
    function resolve2(o, path) {
        let value = o;
        for (let i = 0; i < path.length; i++) {
            const field = path[i];
            const isText = /^\d+$/.exec(field) === null;
            // using instanceof to aid typescript compiler
            if (isText && value instanceof Array) {
                // On the first iteration, we check if we received a stop flag.
                // If so, we stop to prevent iterating over a nested array value
                // on consecutive object keys in the selector.
                if (i === 0 && depth > 0)
                    break;
                depth += 1;
                // only look at the rest of the path
                const subpath = path.slice(i);
                value = value.reduce((acc, item) => {
                    const v = resolve2(item, subpath);
                    if (v !== undefined)
                        acc.push(v);
                    return acc;
                }, []);
                break;
            }
            else {
                value = getValue(value, field);
            }
            if (value === undefined)
                break;
        }
        return value;
    }
    const result = JS_SIMPLE_TYPES.has(getType(obj).toLowerCase())
        ? obj
        : resolve2(obj, selector.split("."));
    return result instanceof Array && (options === null || options === void 0 ? void 0 : options.unwrapArray)
        ? unwrap(result, depth)
        : result;
}
/**
 * Returns the full object to the resolved value given by the selector.
 * This function excludes empty values as they aren't practically useful.
 *
 * @param obj {Object} the object context
 * @param selector {String} dot separated path to field
 */
export function resolveGraph(obj, selector, options) {
    const names = selector.split(".");
    const key = names[0];
    // get the next part of the selector
    const next = names.slice(1).join(".");
    const isIndex = /^\d+$/.exec(key) !== null;
    const hasNext = names.length > 1;
    let result;
    let value;
    if (obj instanceof Array) {
        if (isIndex) {
            result = getValue(obj, Number(key));
            if (hasNext) {
                result = resolveGraph(result, next, options);
            }
            result = [result];
        }
        else {
            result = [];
            for (const item of obj) {
                value = resolveGraph(item, selector, options);
                if (options === null || options === void 0 ? void 0 : options.preserveMissing) {
                    if (value === undefined) {
                        value = MISSING;
                    }
                    result.push(value);
                }
                else if (value !== undefined) {
                    result.push(value);
                }
            }
        }
    }
    else {
        value = getValue(obj, key);
        if (hasNext) {
            value = resolveGraph(value, next, options);
        }
        if (value === undefined)
            return undefined;
        result = (options === null || options === void 0 ? void 0 : options.preserveKeys) ? Object.assign({}, obj) : {};
        result[key] = value;
    }
    return result;
}
/**
 * Filter out all MISSING values from the object in-place
 *
 * @param obj The object to filter
 */
export function filterMissing(obj) {
    if (obj instanceof Array) {
        for (let i = obj.length - 1; i >= 0; i--) {
            if (obj[i] === MISSING) {
                obj.splice(i, 1);
            }
            else {
                filterMissing(obj[i]);
            }
        }
    }
    else if (isObject(obj)) {
        for (const k in obj) {
            if (has(obj, k)) {
                filterMissing(obj[k]);
            }
        }
    }
}
const NUMBER_RE = /^\d+$/;
/**
 * Walk the object graph and execute the given transform function
 *
 * @param  {Object|Array} obj   The object to traverse.
 * @param  {String} selector    The selector to navigate.
 * @param  {Callback} fn Callback to execute for value at the end the traversal.
 * @param  {WalkOptions} options The opetions to use for the function.
 * @return {*}
 */
export function walk(obj, selector, fn, options) {
    const names = selector.split(".");
    const key = names[0];
    const next = names.slice(1).join(".");
    if (names.length === 1) {
        if (isObject(obj) || (isArray(obj) && NUMBER_RE.test(key))) {
            fn(obj, key);
        }
    }
    else {
        // force the rest of the graph while traversing
        if ((options === null || options === void 0 ? void 0 : options.buildGraph) && isNil(obj[key])) {
            obj[key] = {};
        }
        // get the next item
        const item = obj[key];
        // nothing more to do
        if (!item)
            return;
        // we peek to see if next key is an array index.
        const isNextArrayIndex = !!(names.length > 1 && NUMBER_RE.test(names[1]));
        // if we have an array value but the next key is not an index and the 'descendArray' option is set,
        // we walk each item in the array separately. This allows for handling traversing keys for objects
        // nested within an array.
        //
        // Eg: Given { array: [ {k:1}, {k:2}, {k:3} ] }
        //  - individual objecs can be traversed with "array.k"
        //  - a specific object can be traversed with "array.1"
        if (item instanceof Array && (options === null || options === void 0 ? void 0 : options.descendArray) && !isNextArrayIndex) {
            item.forEach(((e) => walk(e, next, fn, options)));
        }
        else {
            walk(item, next, fn, options);
        }
    }
}
/**
 * Set the value of the given object field
 *
 * @param obj {Object|Array} the object context
 * @param selector {String} path to field
 * @param value {*} the value to set. if it is function, it is invoked with the old value and must return the new value.
 */
export function setValue(obj, selector, value) {
    walk(obj, selector, ((item, key) => {
        item[key] = isFunction(value) ? value(item[key]) : value;
    }), { buildGraph: true });
}
/**
 * Removes an element from the container.
 * If the selector resolves to an array and the leaf is a non-numeric key,
 * the remove operation will be performed on objects of the array.
 *
 * @param obj {ArrayOrObject} object or array
 * @param selector {String} dot separated path to element to remove
 */
export function removeValue(obj, selector, options) {
    walk(obj, selector, ((item, key) => {
        if (item instanceof Array) {
            if (/^\d+$/.test(key)) {
                item.splice(parseInt(key), 1);
            }
            else if (options && options.descendArray) {
                for (const elem of item) {
                    if (isObject(elem)) {
                        delete elem[key];
                    }
                }
            }
        }
        else if (isObject(item)) {
            delete item[key];
        }
    }), options);
}
const OPERATOR_NAME_PATTERN = /^\$[a-zA-Z0-9_]+$/;
/**
 * Check whether the given name passes for an operator. We assume AnyVal field name starting with '$' is an operator.
 * This is cheap and safe to do since keys beginning with '$' should be reserved for internal use.
 * @param {String} name
 */
export function isOperator(name) {
    return OPERATOR_NAME_PATTERN.test(name);
}
/**
 * Simplify expression for easy evaluation with query operators map
 * @param expr
 * @returns {*}
 */
export function normalize(expr) {
    // normalized primitives
    if (JS_SIMPLE_TYPES.has(getType(expr).toLowerCase())) {
        return isRegExp(expr) ? { $regex: expr } : { $eq: expr };
    }
    // normalize object expression. using ObjectLike handles custom types
    if (isObjectLike(expr)) {
        const exprObj = expr;
        // no valid query operator found, so we do simple comparison
        if (!Object.keys(exprObj).some(isOperator)) {
            return { $eq: expr };
        }
        // ensure valid regex
        if (has(expr, "$regex")) {
            const newExpr = Object.assign({}, expr);
            newExpr["$regex"] = new RegExp(expr["$regex"], expr["$options"]);
            delete newExpr["$options"];
            return newExpr;
        }
    }
    return expr;
}
